import styles from './fileField.module.css'
import { useEffect, useRef, useState } from 'react'
import { FileTile } from '../../file-tile/fileTile'
import { FileType } from '../../file-tile/types'
import { UploadButton } from '../../upload-button/uploadButton'
import { O, ToastKind } from '../../../state/types'
import { firstItem, toList } from '../../../../../utils/listUtils'
import { fileHelpers, validateFileMimeType, compressImage, MAX_FILE_SIZE_MB } from '../../../../files'
import { showErrorStrToast, showToast } from '../../../actions/toastActions'

type Props = {
    id: string
    value?: File
    fileName?: string
    fileTypes?: FileType[]
    disabled?: boolean
    onChange: (value?: File) => void
}

const FILE_TYPE_LABELS: Partial<Record<FileType, string>> = {
    [FileType.PDF]: 'PDF',
    [FileType.JPEG]: 'JPG',
    [FileType.JPG]: 'JPG',
    [FileType.PNG]: 'PNG',
    [FileType.CSV]: 'CSV',
}

export const FileField = (props: Props) => {
    const { id, value, fileName, fileTypes = toList(FileType.PDF), onChange, disabled } = props

    const [file, setFile] = useState<O<File>>(value)
    const [resetKey, setResetKey] = useState(0)
    const [actualFileType, setActualFileType] = useState<FileType | undefined>()
    const [previewUrl, setPreviewUrl] = useState<string | undefined>()
    const previewUrlRef = useRef<string | undefined>()

    const displayFileType = actualFileType ?? firstItem(fileTypes)!

    useEffect(() => {
        setFile(value)
        if (!value) setActualFileType(undefined)
        revokePreview()
    }, [value])

    useEffect(() => {
        return () => { revokePreview() }
    }, [])

    const revokePreview = () => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current)
            previewUrlRef.current = undefined
        }
        setPreviewUrl(undefined)
    }

    const onChangeFile = async (uploadedFile?: File) => {
        try {
            if (!uploadedFile) {
                setFile(undefined)
                setActualFileType(undefined)
                revokePreview()
                onChange(undefined)
                return
            }

            const allowedMimes = fileHelpers.toAccept(fileTypes).split(',').filter(m => !m.startsWith('.'))
            const isValid = await validateFileMimeType(uploadedFile, allowedMimes)
            if (!isValid) {
                const labels = Array.from(new Set(fileTypes.map(ft => FILE_TYPE_LABELS[ft]).filter(Boolean))).join(', ')
                showErrorStrToast(`Formato no permitido. Formatos aceptados: ${labels}`)
                setResetKey(k => k + 1)
                return
            }

            let processedFile = uploadedFile
            if (processedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                if (processedFile.type.startsWith('image/')) {
                    showToast(`El archivo es grande y se comprimirá automáticamente. Se guardará en formato JPEG.`, ToastKind.YELLOW)
                    const compressed = await compressImage(processedFile)
                    if (!compressed) {
                        showErrorStrToast(`El archivo supera el límite de ${MAX_FILE_SIZE_MB}MB y no pudo comprimirse`)
                        setResetKey(k => k + 1)
                        return
                    }
                    processedFile = compressed
                } else {
                    showErrorStrToast(`El archivo supera el límite de ${MAX_FILE_SIZE_MB}MB`)
                    setResetKey(k => k + 1)
                    return
                }
            }

            const ext = fileHelpers.getFileExtension(processedFile)
            const baseName = (fileName || processedFile.name).replace(/\.[^.]+$/, '')
            const fullName = `${baseName}.${ext}`
            const newFile = new File([processedFile], fullName, { type: processedFile.type })

            const detectedType = fileHelpers.toFileType(processedFile.type)
            revokePreview()
            if (detectedType && (detectedType === FileType.JPEG || detectedType === FileType.JPG || detectedType === FileType.PNG)) {
                const url = URL.createObjectURL(newFile)
                previewUrlRef.current = url
                setPreviewUrl(url)
            }

            setActualFileType(detectedType ?? undefined)
            setFile(newFile)
            onChange(newFile)
        } catch {
            showErrorStrToast('Error al procesar el archivo. Intentá de nuevo.')
            setResetKey(k => k + 1)
        }
    }

    const onClickRemove = () => {
        setActualFileType(undefined)
        revokePreview()
        setFile(undefined)
        onChange(undefined)
    }

    return (
        <div className={styles.container}>
            <FileTile fileType={displayFileType} fileName={file?.name} previewUrl={previewUrl} />
            <div className="pt-2">
                <UploadButton
                    key={resetKey}
                    id={id}
                    fileTypes={fileTypes}
                    defaultValue={file}
                    disabled={disabled}
                    onChange={onChangeFile}
                    onRemove={onClickRemove}
                />
            </div>
        </div>
    )
}
